import { joinEnglishList } from "@/lib/leo-blueprint-section-fill"
import type { LeoAskChoice } from "@/components/assessment-structure/leo-ask-prompt"

export const LEO_BANK_ONLY_INSTRUCTION =
  "Pick replacements from the bank only. Do not write new questions"

export function withLeoBankOnlyInstruction(
  instruction: string,
  bankOnly: boolean,
): string {
  const next = instruction.trim()
  if (!bankOnly || !next) return next
  if (next.includes(LEO_BANK_ONLY_INSTRUCTION)) return next
  return `${next}. ${LEO_BANK_ONLY_INSTRUCTION}`
}

/** Compose Ask Leo must not treat the paper bank suffix as the faculty ask. */
export function stripLeoBankOnlyInstruction(instruction: string): string {
  const text = instruction.trim()
  if (!text) return text
  if (!text.includes(LEO_BANK_ONLY_INSTRUCTION)) return text
  return text
    .replace(LEO_BANK_ONLY_INSTRUCTION, " ")
    .replace(/\s+/g, " ")
    .replace(/\.\s*\./g, ".")
    .replace(/[.\s]+$/g, "")
    .trim()
}

export function leoFillAsk(input: {
  sectionTitle: string
  remaining: number
  missingTopics: readonly string[]
  empty?: boolean
  reviewCount?: number
}): { question: string; choices: LeoAskChoice[] } {
  if (input.empty) {
    return {
      question: `How should Leo start the first draft of ${input.sectionTitle}?`,
      choices: [
        {
          id: "blueprint",
          label: "Follow the blueprint",
          description: "Place bank items that match the planned categories, thinking levels, and types",
          instruction: "Draft this section from the blueprint",
        },
        {
          id: "bank",
          label: "Start from the question bank",
          description: "Place aligned bank items for this section",
          instruction: "Search the bank first and place matching items",
        },
        {
          id: "mix",
          label: "Mix how students answer",
          description: "Single choice, hotspot, short answer, and more",
          instruction: "Mix question types for the first draft",
        },
      ],
    }
  }

  const named = input.missingTopics.filter(Boolean).slice(0, 3)
  const extra = input.missingTopics.length > named.length
  const topicList = joinEnglishList(named)
  const remaining = Math.max(0, input.remaining)
  const gapDetail = topicList
    ? extra
      ? `Still open: ${topicList}, and more`
      : `Still open: ${topicList}`
    : remaining === 1
      ? "1 item still needed on the plan"
      : `${remaining} items still needed on the plan`

  const reviewCount = Math.max(0, input.reviewCount ?? 0)
  const fixChoice =
    reviewCount > 0
      ? [
          {
            id: "fix-issues",
            label: "Fix items that need review",
            description:
              reviewCount === 1
                ? "1 item has weak PBI, heavy reuse, or a review flag"
                : `${reviewCount} items have weak PBI, heavy reuse, or a review flag`,
            instruction: "Only fix items with issues",
          },
        ]
      : []

  return {
    question: `What should Leo add next in ${input.sectionTitle}?`,
    choices: [
      ...fixChoice,
      {
        id: "gaps",
        label: "Cover categories still missing from the blueprint",
        description: gapDetail,
        instruction: topicList
          ? `Cover the missing blueprint categories: ${topicList}`
          : "Cover the missing blueprint categories",
      },
      {
        id: "level",
        label: "Raise the thinking level",
        description: "Write application and analysis items, not recall",
        instruction: "Favor application and analysis items",
      },
      {
        id: "audience",
        label: "Vary how students answer",
        description: "Mix item types so the section is not one format",
        instruction: "Mix question types",
      },
    ],
  }
}

export function leoEnhanceAsk(input: {
  sectionTitle: string
  issueCount?: number
}): { question: string; choices: LeoAskChoice[] } {
  const issues = Math.max(0, input.issueCount ?? 0)
  const issueDetail =
    issues === 0
      ? "Rewrite only items that are not aligned or need review"
      : issues === 1
        ? "1 item is not aligned or needs review"
        : `${issues} items are not aligned or need review`

  return {
    question: `How should Leo enhance ${input.sectionTitle}?`,
    choices: [
      {
        id: "fix-issues",
        label: "Fix items with issues",
        description: issueDetail,
        instruction: "Only fix items with issues",
      },
      {
        id: "rework-existing",
        label: "Rework existing items",
        description: "Rewrite the items already in this section",
        instruction: "Rework existing items in this section",
      },
    ],
  }
}

export function leoReviewAsk(input: {
  sectionTitle: string
  reviewCount: number
}): { question: string; choices: LeoAskChoice[] } {
  const count = Math.max(0, input.reviewCount)
  const reviewDetail =
    count === 1
      ? "1 item is flagged in this section"
      : `${count} items are flagged in this section`

  return {
    question: `Which items should Leo fix in ${input.sectionTitle}?`,
    choices: [
      {
        id: "review",
        label: "Fix items that need review",
        description: reviewDetail,
        instruction: "Only fix items with issues",
      },
      {
        id: "aligned",
        label: "Fix items that miss the blueprint",
        description: "Rewrite items that are not aligned",
        instruction: "Only fix items that are not aligned",
      },
    ],
  }
}
